import { createContext } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { setAuthAction } from "../../redux/actions/authActions/authActions";
import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "../../redux/actions/loadingActions/loadingActions";
import {
  toastErrorMessage,
  toastSuccessMessage,
} from "../../utils/toastMessage/toastMessage";
import {
  confirmRegisterAuthService,
  forgotPasswordAuthService,
  loginAuthService,
  registerAuthService,
  sendLoginOTPAuthService,
  verifyLoginOTPAuthService,
} from "../../services/userServices";

export const authContext = createContext({
  isStaffPage: null,
  handleRegister: () => {},
  handleConfirmRegister: () => {},
  handleLogin: () => {},
  handleSendLoginOtp: () => {},
  handleVerifyLoginOtp: () => {},
  handleForgetPassword: () => {},
});

const AuthContext = ({ children }) => {
  // Redux Dispatch
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { state, pathname } = useLocation();

  const isStaffPage = pathname === "/auth/register/staff";

  const afterLogin = (data) => {
    dispatch(setAuthAction(data.accessToken));
    navigate("/", { replace: true });
  };
  const confirmRegister = (state) => {
    navigate("/auth/register/confirm", {
      replace: true,
      state,
    });
  };

  // Register
  const handleRegister = async (userData) => {
    const objData = {
      ...userData,
      isStaff: isStaffPage,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await registerAuthService(objData);
      if (status === 200) {
        confirmRegister(data.result);
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data?.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Confirm Registration
  const handleConfirmRegister = async (userData) => {
    const signUpToken = state.token;

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await confirmRegisterAuthService(
        userData,
        signUpToken
      );
      if (status === 200) {
        afterLogin(data.result);
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data?.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Login
  const handleLogin = async (userData) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await loginAuthService(userData);
      if (status === 200) {
        afterLogin(data.result);
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          if (response.status === 403) {
            confirmRegister(response.data?.result);
          }
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Send Login Otp
  const handleSendLoginOtp = async (userData, setValue) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status } = await sendLoginOTPAuthService(userData);
      if (status === 200) {
        setValue("phoneNumber", userData.phoneNumber);
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data?.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Verify Login Otp
  const handleVerifyLoginOtp = async (userData) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await verifyLoginOTPAuthService(userData);
      if (status === 200) {
        afterLogin(data.result);
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data?.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Forget Password
  const handleForgetPassword = async (userData) => {
    try {
      dispatch(setLoadingTrueAction());
      const { data, status } = await forgotPasswordAuthService(userData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        navigate("/auth/login", {
          replace: true,
        });
      }
    } catch ({ response }) {
      
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  return (
    <authContext.Provider
      value={{
        isStaffPage,
        handleRegister,
        handleConfirmRegister,
        handleLogin,
        handleSendLoginOtp,
        handleVerifyLoginOtp,
        handleForgetPassword,
      }}
    >
      {children}
    </authContext.Provider>
  );
};

export default AuthContext;
